import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { matchSorter } from 'match-sorter';
import {
  useMediaQuery,
  Grid,
  Checkbox,
  MenuItem,
  Typography,
  Divider,
  Link,
  Collapse,
  Button,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

// COMPONENTS
import CommonDrawer from '@/components/Drawers/CommonDrawer';

// HELP
import SelectedSection from '@/containers/StreamingMarketplace/help/SelectedSection';

// STYLES
import { Wrapper, StyledContolLabel, SelectStyled, CollapseStyled, DrawerContent } from './styles';

const CollapsableContainer = ({
  title,
  children,
  className,
  open,
  handleCollapse,
  ...restProps
}) => {
  return (
    <CollapseStyled {...restProps} className="collapsable">
      <Button
        className={clsx('collapsable__button', className)}
        disableElevation
        disableFocusRipple
        fullWidth
        onClick={handleCollapse}
      >
        <Typography variant="h6">{title}</Typography>
        <KeyboardArrowDownIcon
          className={clsx('collapsable__icon', open && 'collapsable__icon--open')}
          fontSize="inherit"
        />
      </Button>
      <Collapse in={open}>
        <div className={clsx('collapsable__content', className)}>{children}</div>
      </Collapse>
    </CollapseStyled>
  );
};

CollapsableContainer.defaultProps = {
  className: '',
  handleCollapse: () => {},
};

CollapsableContainer.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  handleCollapse: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

const openInitState = {
  types: true,
  channels: false,
  genres: false,
  devices: false,
};

const Filters = props => {
  const {
    openFilters,
    toggleFilters,
    selected,
    options,
    handleChange,
    handleReset,
    loadingFilters,
  } = props;

  const [t] = useTranslation();
  const upMd = useMediaQuery(({ breakpoints }) => breakpoints.up('md'));
  const [state, setState] = useState({
    search: {
      channels: '',
      genres: '',
    },
    open: openInitState,
  });

  const renderCheckbox = ({ name, item }) => {
    const itemValue = item.value || item.id;
    const selectedOptions = selected[name];
    const checked = selectedOptions.includes(itemValue);

    let newValue;

    if (checked) {
      newValue = selectedOptions.filter(el => el !== itemValue);
    } else {
      newValue = [...selectedOptions, itemValue];
    }

    return (
      <StyledContolLabel
        className="control-label"
        control={
          <Checkbox
            className="control-label__checkbox"
            name={name}
            value={itemValue}
            checked={checked}
            onChange={() => handleChange(name, newValue)}
          />
        }
        label={t(item.name || item.text)}
        disabled={loadingFilters}
      />
    );
  };

  const handleSearch = ({ target: { name, value } }) => {
    setState(prevState => ({ ...prevState, search: { ...prevState.search, [name]: value } }));
  };

  const handleOpen = name => () => {
    setState(prevState => {
      const typesState = prevState.open.types;

      if (name === 'types') {
        return {
          ...prevState,
          open: { ...prevState.open, [name]: !prevState.open[name] },
        };
      }

      return {
        ...prevState,
        open: { ...openInitState, types: typesState, [name]: !prevState.open[name] },
      };
    });
  };

  const handleDelete = name => item => () => {
    const selectedData = selected[name].slice();

    const selectedIndex = selectedData.indexOf(item.id);
    selectedData.splice(selectedIndex, 1);

    handleChange(name, selectedData);
  };

  const getSelectedOptions = (a, b) => a.filter(el => b.includes(el.id));
  const selectedChannels = getSelectedOptions(options.channels, selected.channels);
  const selectedGenres = getSelectedOptions(options.genres, selected.genres);

  const matchedGenres = matchSorter(options.genres, state.search.genres, { keys: ['name'] });
  const matchedChannels = matchSorter(options.channels, state.search.channels, { keys: ['name'] });

  const content = (
    <Wrapper className="discover-search-filters filters">
      {selectedChannels.length || selectedGenres.length ? (
        <>
          <div className="filters__container selected-filters">
            <Grid container spacing={upMd ? 4 : 3}>
              {!!selectedChannels.length && (
                <Grid item xs={12}>
                  <SelectedSection
                    data={selectedChannels}
                    title="Available on:"
                    onDelete={handleDelete('channels')}
                  />
                </Grid>
              )}
              {!!selectedGenres.length && (
                <Grid item xs={12}>
                  <SelectedSection
                    data={selectedGenres}
                    title="Tagged with:"
                    onDelete={handleDelete('genres')}
                  />
                </Grid>
              )}
            </Grid>
          </div>
          <div className="filters__container">
            <Link className="filters__reset-filters" onClick={handleReset} underline="always">
              {t('buttons.resetFilters')}
            </Link>
          </div>
        </>
      ) : null}
      <CollapsableContainer
        className="filters__container"
        title="Streaming Apps"
        open={state.open.types}
        handleCollapse={handleOpen('types')}
        disableShadow
      >
        <Grid container spacing={1}>
          {options.types.map((type, index) => (
            <Grid item key={index} xs={12}>
              {renderCheckbox({
                name: 'type',
                item: type,
              })}
            </Grid>
          ))}
        </Grid>
      </CollapsableContainer>
      <Divider />
      <CollapsableContainer
        className="filters__container filters__container--scrollable"
        title="Genres"
        open={state.open.genres}
        handleCollapse={handleOpen('genres')}
        disableShadow
      >
        <TextField
          className="filters__search"
          variant="filled"
          name="genres"
          value={state.search.genres}
          onChange={handleSearch}
          placeholder="Search Genre"
          disabled={loadingFilters}
          InputProps={{
            startAdornment: (
              <InputAdornment className="filters__search__icon" position="start">
                <i className="icon-search" />
              </InputAdornment>
            ),
          }}
        />
        <div className="m-t-15">
          <Grid container spacing={3}>
            {matchedGenres.map((genre, index) => (
              <Grid item key={index} xs={12}>
                {renderCheckbox({
                  name: 'genres',
                  item: genre,
                })}
              </Grid>
            ))}
          </Grid>
        </div>
      </CollapsableContainer>
      <Divider />
      <CollapsableContainer
        className="filters__container filters__container--scrollable"
        title="Similar Channels"
        open={state.open.channels}
        handleCollapse={handleOpen('channels')}
        disableShadow
      >
        <TextField
          className="filters__search"
          variant="filled"
          name="channels"
          value={state.search.channels}
          onChange={handleSearch}
          placeholder="Search Channel"
          disabled={loadingFilters}
          InputProps={{
            startAdornment: (
              <InputAdornment className="filters__search__icon" position="start">
                <i className="icon-search" />
              </InputAdornment>
            ),
          }}
        />

        <div className="m-t-15">
          <Grid container spacing={3}>
            {matchedChannels.map((channel, index) => (
              <Grid item key={index} xs={12}>
                {renderCheckbox({
                  name: 'channels',
                  item: channel,
                })}
              </Grid>
            ))}
          </Grid>
        </div>
      </CollapsableContainer>
      <Divider />
      <CollapsableContainer
        className="filters__container filters__container--scrollable"
        title="Devices"
        open={state.open.devices}
        handleCollapse={handleOpen('devices')}
        disableShadow
      >
        <SelectStyled
          className="select"
          name="devices"
          value={selected.devices}
          onChange={({ target: { name, value } }) => handleChange(name, value)}
          variant="filled"
          displayEmpty
          disabled={loadingFilters}
          disableUnderline
          MenuProps={{
            disablePortal: true,
            transitionDuration: 0,
          }}
        >
          <MenuItem className="select__option" value="">
            {t('common.allDevices')}
          </MenuItem>
          {options.device_groupings.map(device => {
            return (
              <MenuItem key={`device-${device.id}`} className="select__option" value={device.id}>
                {device.name}
              </MenuItem>
            );
          })}
        </SelectStyled>
      </CollapsableContainer>
      <Divider />
    </Wrapper>
  );

  if (!upMd) {
    return (
      <CommonDrawer
        open={openFilters}
        onClose={toggleFilters}
        className="filters-drawer"
        title={t('common.filters')}
      >
        <DrawerContent>{content}</DrawerContent>
      </CommonDrawer>
    );
  }

  return content;
};

Filters.propTypes = {
  openFilters: PropTypes.bool.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  selected: PropTypes.object.isRequired,
  options: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  loadingFilters: PropTypes.bool.isRequired,
};

export default React.memo(Filters);
